import { IUnAssignedOrdersHook } from '@models';
import { UnassignedOrderKind } from '@services';
import { useStore } from '@stores';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoleAccess } from '@hooks';

/** What the panel is filtered by. Every part of it takes effect as soon as it changes. */
interface PanelFilter {
  /** Customer, registration or job title - one box. */
  search: string;
  status: string[];
  /** Regular jobs and/or build legs; none or both mean every job. */
  kinds: Exclude<UnassignedOrderKind, 'All'>[];
  /** The calendar's day, when the list is pinned to it (YYYY-MM-DD). */
  day?: string;
}

const NO_FILTER: PanelFilter = { search: '', status: [], kinds: [] };

/** Typing starts a search after this many characters... */
const MIN_SEARCH_LENGTH = 2;
/** ...once the user has stopped typing for this long. */
const SEARCH_DEBOUNCE_MS = 400;

const toggle = <T,>(list: T[], value: T) =>
  list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

export const useUnAssignedServiceOrders = (
  mobile = false,
  /**
   * The week and month views draw every job that has a day, so the panel is just the ones without
   * (see PagedUnassignedOrderRequest.noDateOnly).
   */
  noDateOnly = false
): IUnAssignedOrdersHook & { loadMore: () => void; hasMore: boolean } => {
  const { orderStore } = useStore();
  const {
    unAssignedItems,
    unAssignedTotalCount,
    unAssignedSkipCount,
    unAssignedLoading,
    unAssignedError,
    unAssignedMaxResultCount,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
  } = orderStore;

  const { sidePanel: canSeePanel } = useRoleAccess().calendar;

  // What is in the box as it is typed, and what has been searched for: the list does not reload
  // on every keystroke, only once the typing has stopped (below).
  const [searchText, setSearchTextValue] = useState('');
  const [applied, setApplied] = useState<PanelFilter>(NO_FILTER);

  const filters = useMemo(
    () => ({
      search: applied.search || undefined,
      // The chips add up (see the API): both kinds ticked is every job, so nothing else narrows it.
      status: applied.kinds.length === 2 ? null : applied.status.length ? applied.status.join(',') : null,
      // One of the two checked is that kind; none or both is no kind chip at all.
      kind: (applied.kinds.length === 1 ? applied.kinds[0] : 'All') as UnassignedOrderKind,
      // The whole of the day, in the garage's own time: local midnight to the end of the day.
      dateFrom: applied.day ? dayjs(applied.day).startOf('day').toISOString() : undefined,
      dateTo: applied.day ? dayjs(applied.day).endOf('day').toISOString() : undefined,
      noDateOnly: noDateOnly || undefined,
    }),
    [applied, noDateOnly]
  );

  const mobilePageSize = 3;
  const mobileSkip = useRef(0);
  const [mobileLoading, setMobileLoading] = useState(false);

  // Day view <-> week/month swaps the list for another one entirely, so the page the old one was on
  // means nothing to it: back to the first page, and the page change re-runs this with it.
  const lastNoDateOnly = useRef(noDateOnly);

  useEffect(() => {
    const viewChanged = lastNoDateOnly.current !== noDateOnly;
    lastNoDateOnly.current = noDateOnly;
    if (viewChanged && !mobile && unAssignedSkipCount !== 0) {
      setUnassignedPageChange(1);
      return undefined;
    }

    if (mobile) {
      mobileSkip.current = 0;
      orderStore.clearUnAssignedItems();
      canSeePanel &&
        orderStore.getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: 0 });
    } else {
      canSeePanel &&
        orderStore.getUnAssignedItems({
          ...filters,
          maxResultCount: unAssignedMaxResultCount,
          skipCount: unAssignedSkipCount,
        });
    }

    // Results accumulate in a Map, so the previous set has to go before the next one lands -
    // otherwise a narrowed filter would still show everything it just excluded.
    return () => orderStore.clearUnAssignedItems();
  }, [
    orderStore,
    mobile ? null : unAssignedSkipCount,
    mobile ? null : unAssignedMaxResultCount,
    canSeePanel,
    filters,
  ]);

  const items = Array.from(unAssignedItems.values());
  const hasMore = items.length < unAssignedTotalCount;

  const loadMore = useCallback(() => {
    if (!mobile || mobileLoading || !hasMore) return;
    const nextSkip = mobileSkip.current + mobilePageSize;
    mobileSkip.current = nextSkip;
    setMobileLoading(true);
    orderStore
      .getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: nextSkip })
      .then(() => setMobileLoading(false));
  }, [mobile, mobileLoading, hasMore, orderStore, filters]);

  /**
   * Every change goes back to the first page, or a narrowed list lands on a page that is now
   * empty. Through setUnassignedPageChange rather than setUnAssignedSkipCount, because the
   * store keeps a page number alongside the skip count and the two must not disagree.
   */
  const resetPaging = useCallback(() => {
    mobileSkip.current = 0;
    setUnassignedPageChange(1);
  }, [setUnassignedPageChange]);

  const update = useCallback(
    (change: Partial<PanelFilter>) => {
      setApplied((a) => ({ ...a, ...change }));
      resetPaging();
    },
    [resetPaging]
  );

  // The search runs on its own once the typing has stopped: a few characters in, and a short pause
  // after the last one.
  useEffect(() => {
    // One character is too little to look for, so it counts as an empty box: a search that was on
    // when the text was cut back to one character comes off, the list never disagrees with the box.
    const text = searchText.trim();
    const wanted = text.length < MIN_SEARCH_LENGTH ? '' : text;
    if (wanted === applied.search) return undefined;
    const timer = setTimeout(() => update({ search: wanted }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText, applied.search, update]);

  const toggleStatus = useCallback(
    (value: string) => update({ status: toggle(applied.status, value) }),
    [applied.status, update]
  );

  const toggleKind = useCallback(
    (value: Exclude<UnassignedOrderKind, 'All'>) => update({ kinds: toggle(applied.kinds, value) }),
    [applied.kinds, update]
  );

  /**
   * Pins the list to one day - the one the calendar is showing - or lets it go again.
   */
  const syncToDay = useCallback((day?: string) => update({ day }), [update]);

  const clearFilters = useCallback(() => {
    setSearchTextValue('');
    setApplied((a) => ({ ...NO_FILTER, day: a.day }));
    resetPaging();
  }, [resetPaging]);

  const hasFilters = !!(applied.search || applied.status.length || applied.kinds.length);

  return {
    unAssignedItems: items,
    unAssignedMaxResultCount: mobile ? mobilePageSize : unAssignedMaxResultCount,
    unAssignedSkipCount: mobile ? mobileSkip.current : unAssignedSkipCount,
    unAssignedLoading: mobile ? mobileLoading : unAssignedLoading,
    unAssignedError,
    unAssignedTotalCount,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
    searchText,
    setSearchText: setSearchTextValue,
    status: applied.status,
    toggleStatus,
    kinds: applied.kinds,
    toggleKind,
    syncToDay,
    clearFilters,
    hasFilters,
    loadMore,
    hasMore,
  };
};
